import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../components/layout/Navigation';
import { Footer } from '../../components/layout/Footer';
import { Container } from '../../components/ui/Container';

const LAST_UPDATED = 'March 2, 2026';

export const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-brand-dark text-white">
      <Navigation />

      <main className="pt-28 pb-16 md:pt-32">
        <Container className="max-w-4xl">
          <section className="rounded-2xl border border-brand-gray-700 bg-brand-gray-900/70 p-7 md:p-10">
            <p className="text-xs uppercase tracking-[0.2em] text-brand-gold">Legal</p>
            <h1 className="mt-3 text-4xl font-bold md:text-5xl">Privacy Policy</h1>
            <p className="mt-3 text-sm text-gray-300">Last updated: {LAST_UPDATED}</p>
            <p className="mt-6 text-gray-200">
              Master Polyglots ("we", "our", "us") values your privacy. This Privacy Policy
              explains what information we collect, how we use it, and the choices you have when
              using our website and services.
            </p>

            <div className="mt-8 space-y-8">
              <section>
                <h2 className="text-2xl font-semibold">1. Information We Collect</h2>
                <p className="mt-3 text-gray-300">
                  We may collect information you provide directly, including your name, email
                  address, course purchases, submitted forms, and support messages. We may also
                  collect technical information such as browser type, device details, IP address,
                  and usage activity on our site.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">2. Payment Information</h2>
                <p className="mt-3 text-gray-300">
                  Payments are processed through third-party payment providers, including Stripe.
                  We do not store full payment card numbers on our servers. Payment processing is
                  subject to the provider&apos;s own terms and privacy practices.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">3. How We Use Information</h2>
                <p className="mt-3 text-gray-300">
                  We use collected information to deliver courses, process purchases, verify access,
                  improve product quality, communicate updates, provide customer support, and protect
                  the security and integrity of our platform.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">4. Cookies and Local Storage</h2>
                <p className="mt-3 text-gray-300">
                  We use browser storage and similar technologies to maintain session state,
                  purchased course access, and basic product functionality. Disabling cookies or
                  local storage may affect site behavior.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">5. Information Sharing</h2>
                <p className="mt-3 text-gray-300">
                  We do not sell personal information. We may share information with trusted service
                  providers who support our operations, including hosting, payment processing,
                  analytics, and communications, and where required by applicable law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">6. Data Retention</h2>
                <p className="mt-3 text-gray-300">
                  We retain information for as long as necessary to provide services, comply with
                  legal obligations, resolve disputes, and enforce agreements.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">7. Security</h2>
                <p className="mt-3 text-gray-300">
                  We apply reasonable technical and organizational safeguards to protect user data.
                  No method of transmission or storage is completely secure, and we cannot guarantee
                  absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">8. Children&apos;s Privacy</h2>
                <p className="mt-3 text-gray-300">
                  Our services are not directed to children under 13, and we do not knowingly
                  collect personal information from children under 13.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">9. Your Rights and Choices</h2>
                <p className="mt-3 text-gray-300">
                  Depending on your location, you may have rights to request access, correction,
                  deletion, or restriction of certain personal information. To make a request,
                  contact us at
                  {' '}
                  <a href="mailto:clintonjohnson@cmjsoftware.com" className="text-brand-gold underline">
                    clintonjohnson@cmjsoftware.com
                  </a>
                  .
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">10. Changes to This Policy</h2>
                <p className="mt-3 text-gray-300">
                  We may update this Privacy Policy from time to time. Updates are effective when
                  posted, and the "Last updated" date above will reflect the latest version.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold">11. Contact</h2>
                <p className="mt-3 text-gray-300">
                  For privacy-related questions, contact
                  {' '}
                  <a href="mailto:clintonjohnson@cmjsoftware.com" className="text-brand-gold underline">
                    clintonjohnson@cmjsoftware.com
                  </a>
                  .
                </p>
              </section>
            </div>

            <p className="mt-10 text-sm text-gray-400">
              You can also review our
              {' '}
              <Link to="/terms" className="text-brand-gold underline">
                Terms of Service
              </Link>
              .
            </p>
          </section>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

